import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import useInternalBooking from '../../hooks/useInternalBooking';
import useEntertainment from '../../hooks/useEntertainment';
import useBranch from '../../hooks/useBranch';
import useLocationStatus from '../../hooks/useLocationStatus';
import dayjs from 'dayjs';
import { NumberChip } from '../Bookings/components/BookingIcons';
import GroupNameDisplay from '../Bookings/components/GroupNameDisplay';
import CountdownTimer from '../Bookings/Briefing/components/CountdownTimer';
import BriefingCheckButton from '../Bookings/Briefing/components/BriefingCheckButton';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import { toast } from 'react-toastify';
import useMediaQuery from '@mui/material/useMediaQuery';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function BriefingView() {
  const theme = useTheme();
  const slotColors = useMemo(
    () => [
      {
        name: 'Grüner Slot',
        color: 'green',
        hex: theme.palette.success.main,
        audioFileName: 'briefing-gruen.mp3',
      },
      {
        name: 'Oranger Slot',
        color: 'orange',
        hex: theme.palette.warning.main,
        audioFileName: 'briefing-orange.mp3',
      },
      {
        name: 'Ohne Slot',
        color: 'without-slot',
        hex: theme.palette.grey[500],
        audioFileName: null,
      },
    ],
    [theme]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openBriefings, setOpenBriefings] = useState([]);
  const [upcomingNextBriefings, setUpcomingNextBriefings] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    getOpenBriefings,
    getUpcomingNextBookings,
    markCustomerAsPresent,
    markLTCustomerAsBriefed,
  } = useInternalBooking();
  const { getAllEntertainmentsByBranch } = useEntertainment();
  const { getBranches } = useBranch();
  const [lateCheckinModalOpen, setLateCheckinModalOpen] = useState(false);
  const [lateCheckinReason, setLateCheckinReason] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState({});
  const [bookingErrors, setBookingErrors] = useState({});
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [entertainment, setEntertainment] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [branch, setBranch] = useState('');
  const [prepTimeMinutes, setPrepTimeMinutes] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [videoControlsExpanded, setVideoControlsExpanded] = useState(false);

  const hasLoadedBriefingsRef = useRef(false);

  // Location status and commands
  const { briefingStatus, audioStatus, sendCommand } = useLocationStatus(entertainment);

  // Fetch branches when component mounts
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branches = await getBranches();
        const shownBranches = branches.map(branch => ({
          label: branch.locationName,
          id: branch._id,
        }));

        setBranchOptions(shownBranches);

        // Set default branch from storage or fallback to first
        let initialBranch = '';
        try {
          const storedBranch = sessionStorage.getItem('branch');
          if (storedBranch && shownBranches.some(b => b.id === storedBranch)) {
            initialBranch = storedBranch;
          } else if (shownBranches.length > 0) {
            initialBranch = shownBranches[0].id;
          }
        } catch (_) {
          if (shownBranches.length > 0) {
            initialBranch = shownBranches[0].id;
          }
        }
        setBranch(initialBranch || '');
      } catch (err) {
        console.error('Error fetching branches:', err);
      }
    };

    fetchBranches();
  }, []);

  // Fetch entertainments when branch changes
  useEffect(() => {
    const fetchEntertainments = async () => {
      if (!branch) return;

      try {
        const entertainments = await getAllEntertainmentsByBranch(branch);

        const laserEntertainments = (entertainments || []).filter(ent => {
          if (!ent || typeof ent.entertainmentName !== 'string') {
            return false;
          }
          return ent.entertainmentName.trim().toLowerCase() === 'laserzone';
        });

        const shownEntertainments = laserEntertainments.map(entertainment => ({
          id: entertainment._id,
          label: entertainment.entertainmentName,
          prepTimeMinutes: entertainment.prepTimeMinutes,
        }));

        setEntertainmentOptions(shownEntertainments);

        // Set default entertainment if available
        if (shownEntertainments.length > 0) {
          setEntertainment(shownEntertainments[0].id);
        } else {
          setEntertainment('');
        }
      } catch (err) {
        console.error('Error fetching entertainments:', err);
      }
    };

    fetchEntertainments();
  }, [branch]);

  useEffect(() => {
    if (!entertainment) return;

    const selectedEntertainment = entertainmentOptions.find(e => e.id === entertainment);
    setPrepTimeMinutes(selectedEntertainment?.prepTimeMinutes ?? null);

    fetchBriefings();

    const interval = setInterval(() => {
      fetchBriefings({ showLoading: false });
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [entertainment, entertainmentOptions]);

  const fetchBriefings = async ({ showLoading = !hasLoadedBriefingsRef.current } = {}) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const [openResponse, upcomingResponse] = await Promise.all([
        getOpenBriefings({ entertainmentId: entertainment }),
        getUpcomingNextBookings({ entertainmentId: entertainment }),
      ]);

      if (openResponse && Array.isArray(openResponse)) {
        setOpenBriefings(openResponse);
      } else {
        toast.error('Unerwartetes Antwortformat von der API');
      }

      if (upcomingResponse && Array.isArray(upcomingResponse)) {
        setUpcomingNextBriefings(upcomingResponse);
      } else {
        toast.error('Unerwartetes Antwortformat von der API');
      }
    } catch (err) {
      toast.error('Fehler beim Laden der Briefings');
      console.error('Error fetching briefings:', err);
    } finally {
      hasLoadedBriefingsRef.current = true;
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleMarkAsPresent = async bookingId => {
    const booking = upcomingNextBriefings.find(b => b._id === bookingId);
    if (!booking) return;

    const now = new Date();
    const bookingStartTime = new Date(booking.startTime);
    const isLate = now > bookingStartTime - prepTimeMinutes * 60000;

    if (isLate) {
      setSelectedBookingId(bookingId);
      setLateCheckinModalOpen(true);
    } else {
      await markAsPresent(bookingId);
    }
  };

  const markAsPresent = useCallback(
    async bookingId => {
      try {
        setLoadingBookings(prev => ({ ...prev, [bookingId]: true }));
        setBookingErrors(prev => ({ ...prev, [bookingId]: null }));

        const result = await markCustomerAsPresent({
          bookingId,
          body: {
            lateArrivalReason: lateCheckinReason || null,
          },
        });

        if (result && result.updatedBooking && result.updatedBooking.presentSince) {
          setLateCheckinModalOpen(false);
          setLateCheckinReason('');

          fetchBriefings();
        }
      } catch (err) {
        setBookingErrors(prev => ({ ...prev, [bookingId]: 'Fehler' }));
        console.error(err);
      } finally {
        setLoadingBookings(prev => ({ ...prev, [bookingId]: false }));
      }
    },
    [markCustomerAsPresent, lateCheckinReason, fetchBriefings]
  );

  const handleLateCheckinSubmit = useCallback(async () => {
    if (lateCheckinReason.length >= 10) {
      await markAsPresent(selectedBookingId);
    }
  }, [lateCheckinReason, selectedBookingId, markAsPresent]);

  const handleBranchChange = event => {
    const value = event.target.value;
    setBranch(value);
    try {
      sessionStorage.setItem('branch', value);
    } catch (_) {}
  };

  const handleMarkAsBriefed = useCallback(async bookingId => {
    await markAsBriefed(bookingId);
  }, []);

  const markAsBriefed = useCallback(async bookingId => {
    try {
      setLoadingBookings(prev => ({ ...prev, [bookingId]: true }));
      setBookingErrors(prev => ({ ...prev, [bookingId]: null }));

      const response = await markLTCustomerAsBriefed({ bookingId });
      if (response) {
        setOpenBriefings(prev => prev.filter(booking => booking._id !== bookingId));
      } else {
        setBookingErrors(prev => ({ ...prev, [bookingId]: 'Fehler' }));
      }
    } catch (err) {
      setBookingErrors(prev => ({ ...prev, [bookingId]: 'Fehler' }));
      console.error(err);
    } finally {
      setLoadingBookings(prev => ({ ...prev, [bookingId]: false }));
    }
  }, []);

  const hasAssignedSlot = playerGroupID => playerGroupID === 0 || playerGroupID === 1;

  // Separate briefings by slot assignment
  const briefingsByColor = slotColors.map(slot => ({
    ...slot,
    briefings: openBriefings.filter(booking => {
      const playerGroupID = booking.ltSpecific?.playerGroupID;
      if (slot.color === 'green') {
        return playerGroupID === 1;
      } else if (slot.color === 'orange') {
        return playerGroupID === 0;
      } else if (slot.color === 'without-slot') {
        return !hasAssignedSlot(playerGroupID);
      }
      return false;
    }),
  }));

  const handleOpenDetailsModal = bookingId => {
    setSelectedBookingId(bookingId);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedBookingId(null);
  };

  const BriefingList = ({ slot }) => {
    const audioFileName = slot.audioFileName;
    const isAudioPlaying = audioStatus.status === 'playing';

    return (
      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          mb: 2,
          borderLeft: `4px solid ${slot.hex}`,
          backgroundColor: `${slot.hex}10`,
          '&:focus': {
            borderColor: 'success.main',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='h6'>{slot.name}</Typography>
            {slot.briefings.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NumberChip number={slot.briefings.length} size='small' color='error.main' />
              </Box>
            )}
          </Stack>
          {slot.briefings.length > 0 && audioFileName && (
            <Tooltip
              title={
                isAudioPlaying
                  ? 'Ansage läuft'
                  : audioStatus.status !== 'online'
                    ? 'Audio-System nicht verfügbar'
                    : 'Ansage starten'
              }
            >
              <span>
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  disabled={audioStatus.status !== 'online'}
                  startIcon={isAudioPlaying ? <VolumeUpIcon /> : null}
                  onClick={async () => {
                    const success = await sendCommand('start', audioFileName, 'audio');
                    if (success) {
                      toast.success(`Ansage ${slot.name} gestartet`);
                    }
                  }}
                >
                  {isAudioPlaying ? 'Ansage läuft' : 'Ansage starten'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        {slot.briefings.length === 0 ? (
          <Typography color='text.secondary'>Keine offenen Briefings</Typography>
        ) : (
          <Stack spacing={2}>
            {slot.briefings.map(booking => (
              <Paper
                key={booking._id}
                sx={{ p: { xs: 1, sm: 2 }, cursor: 'pointer' }}
                onClick={() => handleOpenDetailsModal(booking._id)}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={1}
                  sx={{ width: '100%' }}
                >
                  <Stack
                    direction='column'
                    alignItems='flex-start'
                    spacing={0.5}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Typography variant='subtitle1'>
                        {booking.firstName} {booking.lastName}
                      </Typography>
                      <Box sx={{ pt: '3px' }}>
                        <NumberChip
                          number={booking.participantCount}
                          size='small'
                          color='primary'
                        />
                      </Box>
                      {(Array.isArray(booking.tableNumbers) && booking.tableNumbers.length > 0
                        ? booking.tableNumbers
                        : booking.tableNumber
                          ? [booking.tableNumber]
                          : []
                      ).map(tableNumber => (
                        <Box key={tableNumber} sx={{ pt: '3px' }}>
                          <Chip
                            icon={<TableRestaurantIcon />}
                            label={`Tisch ${tableNumber}`}
                            size='small'
                            variant='outlined'
                          />
                        </Box>
                      ))}
                    </Stack>
                    {booking.ltSpecific?.groupName && (
                      <Typography
                        variant='body2'
                        sx={{
                          fontStyle: 'italic',
                          fontWeight: 500,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: 'action.hover',
                        }}
                      >
                        {'\u201E'}
                        <GroupNameDisplay name={booking.ltSpecific.groupName} />
                        {'"'}
                      </Typography>
                    )}
                    <Typography variant='body2' color='text.secondary'>
                      Start: {dayjs(booking.startTime).format('HH:mm')} Uhr
                    </Typography>
                  </Stack>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent={{ xs: 'space-between', md: 'flex-end' }}
                    spacing={2}
                    sx={{ flexShrink: 0 }}
                  >
                    <CountdownTimer startTime={booking.startTime} />
                    <BriefingCheckButton
                      bookingId={booking._id}
                      onAction={handleMarkAsBriefed}
                      isLoading={loadingBookings[booking._id]}
                      error={bookingErrors[booking._id]}
                      actionType='briefing'
                    />
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    );
  };

  const UpcomingList = () => {
    return (
      <Paper
        sx={{
          p: { xs: 1, sm: 2 },
          mb: 2,
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        <Typography variant='h6' gutterBottom>
          Nächste Ankünfte
        </Typography>
        {upcomingNextBriefings.length === 0 ? (
          <Typography color='text.secondary'>Keine anstehenden Ankünfte</Typography>
        ) : (
          <Stack spacing={2}>
            {upcomingNextBriefings.map(booking => (
              <Paper
                key={booking._id}
                sx={{ p: { xs: 1, sm: 2 }, cursor: 'pointer' }}
                onClick={() => handleOpenDetailsModal(booking._id)}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent='space-between'
                  alignItems={{ xs: 'stretch', md: 'center' }}
                  spacing={1}
                  sx={{ width: '100%' }}
                >
                  <Stack
                    direction='column'
                    alignItems='flex-start'
                    spacing={0.5}
                    sx={{ flex: 1, minWidth: 0 }}
                  >
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Typography variant='subtitle1'>
                        {booking.firstName} {booking.lastName}
                      </Typography>
                      <Box sx={{ pt: '3px' }}>
                        <NumberChip
                          number={booking.participantCount}
                          size='small'
                          color='primary'
                        />
                      </Box>
                      {(Array.isArray(booking.tableNumbers) && booking.tableNumbers.length > 0
                        ? booking.tableNumbers
                        : booking.tableNumber
                          ? [booking.tableNumber]
                          : []
                      ).map(tableNumber => (
                        <Box key={tableNumber} sx={{ pt: '3px' }}>
                          <Chip
                            icon={<TableRestaurantIcon />}
                            label={`Tisch ${tableNumber}`}
                            size='small'
                            variant='outlined'
                          />
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant='body2' color='text.secondary'>
                      Start: {dayjs(booking.startTime).format('HH:mm')} Uhr
                    </Typography>
                  </Stack>
                  <Box sx={{ flexShrink: 0 }}>
                    <BriefingCheckButton
                      bookingId={booking._id}
                      onAction={handleMarkAsPresent}
                      isLoading={loadingBookings[booking._id]}
                      error={bookingErrors[booking._id]}
                      actionType='present'
                      text='Als anwesend markieren'
                    />
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...viewRootTextPrimarySx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const onUpdate = updatedBooking => {
    // If the booking is now present, move it from upcoming to open briefings
    if (updatedBooking.presentSince) {
      // Remove from upcoming briefings
      setUpcomingNextBriefings(prev => prev.filter(booking => booking._id !== updatedBooking._id));

      // Add to open briefings
      setOpenBriefings(prev => {
        // Check if booking already exists in open briefings
        const exists = prev.some(booking => booking._id === updatedBooking._id);
        if (!exists) {
          return [...prev, updatedBooking];
        }
        return prev.map(booking =>
          booking._id === updatedBooking._id ? { ...booking, ...updatedBooking } : booking
        );
      });
    } else {
      // Update both lists with the new data
      setOpenBriefings(prev =>
        prev.map(booking =>
          booking._id === updatedBooking._id ? { ...booking, ...updatedBooking } : booking
        )
      );

      setUpcomingNextBriefings(prev =>
        prev.map(booking =>
          booking._id === updatedBooking._id ? { ...booking, ...updatedBooking } : booking
        )
      );
    }
  };

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 1, sm: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent='space-between'
        sx={{ mb: 3 }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h5'>Offene Briefings</Typography>
          {openBriefings.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NumberChip number={openBriefings.length} size='small' color='error.main' />
            </Box>
          )}
        </Stack>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{
            width: { xs: '100%', md: 'auto' },
            alignItems: { xs: 'stretch', md: 'center' },
          }}
        >
          {branchOptions.length > 1 && (
            <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
              <InputLabel>Branch</InputLabel>
              <Select value={branch} label='Branch' onChange={handleBranchChange} size='small'>
                {branchOptions.map(br => (
                  <MenuItem key={br.id} value={br.id}>
                    {br.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {entertainment && (
            <Tooltip
              title={
                audioStatus.status === 'playing'
                  ? 'Ansage läuft'
                  : audioStatus.status !== 'online'
                    ? 'Audio-System nicht verfügbar'
                    : 'Ansage Briefing alle starten'
              }
            >
              <span>
                <Button
                  variant='outlined'
                  color='primary'
                  size='small'
                  disabled={audioStatus.status !== 'online'}
                  startIcon={audioStatus.status === 'playing' ? <VolumeUpIcon /> : null}
                  onClick={async () => {
                    const success = await sendCommand('start', 'briefing-now.mp3', 'audio');
                    if (success) {
                      toast.success('Ansage Briefing alle gestartet');
                    }
                  }}
                  sx={{ ml: { xs: 0, md: 'auto' } }}
                >
                  {audioStatus.status === 'playing' ? 'Ansage läuft' : 'Ansage Briefing alle'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Video Briefing Controls */}
      {entertainment && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            borderLeft:
              briefingStatus.status === 'last30' || briefingStatus.status === 'waitingForEmployee'
                ? `4px solid ${theme.palette.warning.dark}`
                : 'none',
            backgroundColor:
              briefingStatus.status === 'last30' || briefingStatus.status === 'waitingForEmployee'
                ? theme.palette.warning.main
                : 'background.paper',
            color:
              briefingStatus.status === 'last30' || briefingStatus.status === 'waitingForEmployee'
                ? theme.palette.getContrastText(theme.palette.warning.main)
                : undefined,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
                <VideoLibraryIcon color='primary' />
                <Typography variant='h6'>Briefing-Steuerung</Typography>
              </Stack>
              <Stack direction='row' flexWrap='wrap' alignItems='center' useFlexGap sx={{ gap: 1 }}>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={
                    !['looping', 'waitingForEmployee', 'webview'].includes(briefingStatus.status)
                  }
                  startIcon={briefingStatus.status === 'running' ? <PlayCircleIcon /> : null}
                  onClick={async () => {
                    const success = await sendCommand('start', 'de', 'briefing');
                    if (success) {
                      toast.success('Briefing auf Deutsch gestartet');
                    }
                  }}
                >
                  {briefingStatus.status === 'running' ? 'Briefing läuft' : 'Briefing Deutsch'}
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={
                    !['looping', 'waitingForEmployee', 'webview'].includes(briefingStatus.status)
                  }
                  startIcon={briefingStatus.status === 'running' ? <PlayCircleIcon /> : null}
                  onClick={async () => {
                    const success = await sendCommand('start', 'en', 'briefing');
                    if (success) {
                      toast.success('Briefing mit Untertiteln gestartet');
                    }
                  }}
                >
                  {briefingStatus.status === 'running'
                    ? 'Briefing läuft'
                    : 'Briefing mit Untertiteln'}
                </Button>
                {briefingStatus.status === 'webview' && (
                  <Tooltip title='Teameinteilung wird gerade auf dem Briefing-Bildschirm angezeigt'>
                    <Stack
                      direction='row'
                      spacing={0.5}
                      alignItems='center'
                      sx={{ color: 'warning.main' }}
                    >
                      <WarningIcon fontSize='small' />
                      <Typography variant='caption' sx={{ fontWeight: 'bold' }}>
                        Teameinteilung aktiv
                      </Typography>
                    </Stack>
                  </Tooltip>
                )}
                {['running', 'last30', 'waitingForEmployee'].includes(briefingStatus.status) && (
                  <Tooltip title='Video stoppen'>
                    <IconButton
                      color='error'
                      onClick={async () => {
                        const success = await sendCommand('stop', 'stop', 'briefing');
                        if (success) {
                          toast.success('Briefing gestoppt');
                        }
                      }}
                    >
                      <StopCircleIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
            <IconButton
              onClick={() => setVideoControlsExpanded(!videoControlsExpanded)}
              aria-expanded={videoControlsExpanded}
              aria-label='Status anzeigen'
              sx={{ flexShrink: 0 }}
            >
              {videoControlsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          {(briefingStatus.status === 'last30' ||
            briefingStatus.status === 'waitingForEmployee') && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: theme.palette.warning.dark,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <WarningIcon sx={{ color: theme.palette.getContrastText(theme.palette.warning.dark) }} />
              <Typography
                variant='body1'
                sx={{
                  color: theme.palette.getContrastText(theme.palette.warning.dark),
                  fontWeight: 'bold',
                }}
              >
                {briefingStatus.status === 'last30'
                  ? 'Warnung: Weniger als 30 Sekunden verbleibend!'
                  : 'Warte auf Mitarbeiter - Bitte Aktion durchführen!'}
              </Typography>
            </Box>
          )}
          <Collapse in={videoControlsExpanded}>
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Briefing-Status: {briefingStatus.displayStatus}
              </Typography>
              {briefingStatus.receivedAt && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1 }}
                >
                  Letzte Änderung: {dayjs(briefingStatus.receivedAt).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
              )}
              <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                Audio-Status: {audioStatus.displayStatus}
              </Typography>
              {audioStatus.receivedAt && (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                  Letzte Änderung: {dayjs(audioStatus.receivedAt).format('DD.MM.YYYY HH:mm:ss')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      {briefingsByColor.map(slot => (
        <BriefingList key={slot.color} slot={slot} />
      ))}

      <UpcomingList />

      <Dialog
        open={lateCheckinModalOpen}
        onClose={() => setLateCheckinModalOpen(false)}
        maxWidth='sm'
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          },
        }}
      >
        <DialogTitle>Verspätete Ankunft dokumentieren</DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Der Kunde ist verspätet eingetroffen. Bitte gib einen Grund für die Verspätung an
            (mindestens 10 Zeichen).
          </Typography>
          <TextField
            value={lateCheckinReason}
            onChange={e => setLateCheckinReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            variant='outlined'
            placeholder='Grund für die Verspätung...'
            error={lateCheckinReason.length > 0 && lateCheckinReason.length < 10}
            helperText={
              lateCheckinReason.length > 0 && lateCheckinReason.length < 10
                ? 'Mindestens 10 Zeichen erforderlich'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLateCheckinModalOpen(false)} color='error'>
            Abbrechen
          </Button>
          <Button
            onClick={handleLateCheckinSubmit}
            variant='contained'
            color='success'
            disabled={lateCheckinReason.length < 10}
          >
            Bestätigen
          </Button>
        </DialogActions>
      </Dialog>

      <BookingDetailsModal
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        bookingId={selectedBookingId}
        onUpdate={onUpdate}
      />
    </Box>
  );
}

export default BriefingView;
